import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useSeatPersonalization } from '@/hooks/useSeatPersonalization';
import { useSessionFilters } from '@/hooks/useSessionFilters';
import { useDashboardPreferences } from '@/hooks/useDashboardPreferences';
import { supabase } from '@/integrations/supabase/client';
import { UserMenu } from '@/components/UserMenu';
import { WorkspaceAvatar } from '@/components/WorkspaceAvatar';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { QuickStartCard } from '@/components/dashboard/QuickStartCard';
import { WelcomeDialog } from '@/components/dashboard/WelcomeDialog';
import { TeamManagementSheet } from '@/components/dashboard/TeamManagementSheet';
import { SessionSearch } from '@/components/dashboard/SessionSearch';
import { SessionSort } from '@/components/dashboard/SessionSort';
import { SessionFilters } from '@/components/dashboard/SessionFilters';
import { ActiveSessionsList } from '@/components/dashboard/ActiveSessionsList';
import { DashboardSidebar } from '@/components/dashboard/DashboardSidebar';
import { NewDecisionSheet } from '@/components/dashboard/NewDecisionSheet';
import { BottomNavigation } from '@/components/dashboard/BottomNavigation';
import { SeatUpgradeBanner } from '@/components/dashboard/SeatUpgradeBanner';
import { InsightsPanel } from '@/components/dashboard/InsightsPanel';
import { HypothesesTracker } from '@/components/dashboard/HypothesesTracker';
import { TeamActivityFeed } from '@/components/dashboard/TeamActivityFeed';
import { CommandPalette } from '@/components/CommandPalette';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  SessionsListSkeleton,
  QuickStartSkeleton,
  DashboardHeaderSkeleton,
} from '@/components/dashboard/skeletons/DashboardSkeletons';
import quentariLogo from '@/assets/quentari-logo.png';
import { Loader2, Users, Command, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [checkingWorkspace, setCheckingWorkspace] = useState(true);
  const [showNewDecisionSheet, setShowNewDecisionSheet] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTeamManagement, setShowTeamManagement] = useState(false);
  const [showMobileInsights, setShowMobileInsights] = useState(false);
  const [activeBottomTab, setActiveBottomTab] = useState('home');

  const { personalization, loading: personalizationLoading } = useSeatPersonalization(
    workspaceId,
    user?.id || null
  );

  const { stats, activeSessions, teamActivities, userProfile, seatInfo, loading: dashboardLoading } = useDashboardData(workspaceId);
  
  const {
    preferences,
    updateFilters,
    updateSortOptions,
    updateActiveInsightsTab,
  } = useDashboardPreferences();

  const { filteredSessions, activeFilterCount } = useSessionFilters(
    activeSessions,
    preferences.filters,
    preferences.sortOptions
  );

  // Check workspace membership
  useEffect(() => {
    async function checkWorkspace() {
      // CRITICAL: Wait until auth is loaded
      if (authLoading) return;
      
      if (!user) {
        setCheckingWorkspace(false);
        navigate('/');
        return;
      }

      try {
        const { data: memberships } = await supabase
          .from('workspace_members')
          .select('workspace_id, personalization_completed_at')
          .eq('user_id', user.id)
          .eq('seat_status', 'active');

        if (memberships && memberships.length > 0) {
          setWorkspaceId(memberships[0].workspace_id);

          // If personalization not completed, redirect to onboarding
          if (!memberships[0].personalization_completed_at) {
            navigate('/onboarding');
            return;
          }
        } else {
          navigate('/onboarding');
          return;
        }
      } catch (error) {
        console.error('Error checking workspace:', error);
        navigate('/onboarding');
      } finally {
        setCheckingWorkspace(false);
      }
    }

    checkWorkspace();
  }, [user, authLoading, navigate]);

  // Check if this is first visit
  useEffect(() => {
    if (workspaceId && userProfile) {
      const hasSeenWelcome = localStorage.getItem(`welcome-seen-${user?.id}`);
      if (!hasSeenWelcome) {
        setShowWelcome(true);
      }
    }
  }, [workspaceId, userProfile, user?.id]);

  const handleCloseWelcome = () => {
    setShowWelcome(false);
    if (user?.id) {
      localStorage.setItem(`welcome-seen-${user.id}`, 'true');
    }
  };

  if (authLoading || checkingWorkspace || !workspaceId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Wird geladen...</p>
        </div>
      </div>
    );
  }

  const preferredAreas = personalization?.preferred_decision_areas || [];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div 
          className="absolute top-0 left-0 w-full h-full opacity-[0.02]"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 60% 40% at 10% 20%, hsl(var(--primary)) 0%, transparent 50%),
              radial-gradient(ellipse 50% 50% at 90% 80%, hsl(var(--primary)) 0%, transparent 50%)
            `,
          }}
        />
        <div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="container flex h-14 sm:h-16 items-center justify-between max-w-7xl mx-auto px-3 sm:px-4 md:px-6">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img
              src={quentariLogo}
              alt="Quentari"
              className="h-6 sm:h-8 w-auto cursor-pointer transition-transform duration-300 hover:scale-105 flex-shrink-0"
              onClick={() => navigate('/dashboard')}
            />
            <div className="h-5 sm:h-6 w-px bg-border hidden xs:block" aria-hidden="true" />
            <div className="hidden xs:block">
              <WorkspaceAvatar size="sm" showName={false} />
            </div>
            <div className="hidden sm:block">
              <WorkspaceAvatar size="sm" showName />
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Desktop: Command Palette */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const event = new KeyboardEvent('keydown', { key: 'k', metaKey: true });
                    document.dispatchEvent(event);
                  }}
                  className="hidden sm:flex border-border/50 hover:bg-primary/5 hover:border-primary/20 text-muted-foreground hover:text-primary gap-2"
                >
                  <Command className="h-3.5 w-3.5" />
                  <span className="text-xs">⌘K</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Schnellzugriff öffnen</p>
              </TooltipContent>
            </Tooltip>
            {/* Desktop: Full buttons */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTeamManagement(true)}
              className="hidden sm:flex border-primary/20 hover:bg-primary/5 hover:border-primary/30 hover:text-primary"
            >
              <Users className="h-4 w-4 mr-2" />
              Team
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/settings/workspace')}
              className="hidden sm:flex hover:bg-primary/5 hover:text-primary"
            >
              <Settings className="h-4 w-4 mr-2" />
              Einstellungen
            </Button>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="container max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 relative z-10 pb-24 lg:pb-8">
        {/* Seat Upgrade Banner */}
        {seatInfo && (
          <SeatUpgradeBanner usedSeats={seatInfo.usedSeats} maxSeats={seatInfo.maxSeats} />
        )}

        <div className="stagger-1">
          {dashboardLoading ? (
            <DashboardHeaderSkeleton />
          ) : (
            <DashboardHeader
              userName={userProfile?.first_name || userProfile?.name || user?.email?.split('@')[0] || null}
              stats={stats}
              activeSessions={activeSessions}
            />
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Quick Actions & Sessions */}
          <div className="lg:col-span-2 space-y-6">
            <div className="stagger-2">
              {dashboardLoading ? (
                <QuickStartSkeleton />
              ) : (
                <QuickStartCard
                  preferredAreas={preferredAreas}
                  onNewDecision={() => setShowNewDecisionSheet(true)}
                  onTeamManagement={() => setShowTeamManagement(true)}
                />
              )}
            </div>

            <div className="space-y-4 stagger-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <SessionSearch
                    value={preferences.filters.searchQuery}
                    onChange={(value) => updateFilters({ ...preferences.filters, searchQuery: value })}
                    resultCount={preferences.filters.searchQuery.trim() ? filteredSessions.length : undefined}
                  />
                </div>
                <SessionSort
                  sortOptions={preferences.sortOptions}
                  onSortChange={updateSortOptions}
                />
              </div>

              {activeSessions.length > 0 && (
                <SessionFilters
                  filters={preferences.filters}
                  onFilterChange={updateFilters}
                  activeFilterCount={activeFilterCount}
                />
              )}
            </div>

            <div className="stagger-4">
              {dashboardLoading ? (
                <SessionsListSkeleton />
              ) : (
                <ActiveSessionsList 
                  sessions={filteredSessions} 
                  showEmptyState={activeFilterCount === 0}
                  onNewDecision={() => setShowNewDecisionSheet(true)}
                />
              )}
            </div>
          </div>

          {/* Right Column - Tabbed Sidebar */}
          <div className="stagger-3" data-sidebar-section>
            <DashboardSidebar
              sessions={activeSessions}
              teamActivities={teamActivities}
              workspaceId={workspaceId}
              isLoading={dashboardLoading}
              activeInsightsTab={preferences.activeInsightsTab}
              onInsightsTabChange={updateActiveInsightsTab}
            />
          </div>
        </div>
      </main>

      {personalization && workspaceId && (
        <NewDecisionSheet
          open={showNewDecisionSheet}
          onOpenChange={setShowNewDecisionSheet}
          workspaceId={workspaceId}
          personalization={personalization}
          firstName={userProfile?.first_name}
        />
      )}

      {userProfile && (
        <WelcomeDialog
          open={showWelcome}
          onClose={handleCloseWelcome}
          firstName={userProfile.first_name || ''}
          onStartDecision={() => setShowNewDecisionSheet(true)}
          onTeamManagement={() => setShowTeamManagement(true)}
        />
      )}

      {workspaceId && user && (
        <TeamManagementSheet
          open={showTeamManagement}
          onOpenChange={setShowTeamManagement}
          workspaceId={workspaceId}
          userId={user.id}
        />
      )}

      <CommandPalette 
        onNewDecision={() => setShowNewDecisionSheet(true)}
        onTeamManagement={() => setShowTeamManagement(true)}
      />

      {/* Mobile Bottom Navigation */}
      <BottomNavigation
        activeTab={activeBottomTab}
        onTabChange={(tab) => {
          setActiveBottomTab(tab);
          if (tab === 'home') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else if (tab === 'team') {
            setShowTeamManagement(true);
          } else if (tab === 'insights') {
            setShowMobileInsights(true);
          }
        }}
        onNewDecision={() => setShowNewDecisionSheet(true)}
      />

      {/* Mobile Insights Sheet */}
      <Sheet open={showMobileInsights} onOpenChange={setShowMobileInsights}>
        <SheetContent side="bottom" className="h-[85vh] rounded-t-2xl overflow-hidden">
          <SheetHeader className="pb-4 border-b border-border/50">
            <SheetTitle className="text-lg font-semibold">Insights & Analytics</SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground">
              Muster, Statistiken und Empfehlungen
            </SheetDescription>
          </SheetHeader>
          <div className="overflow-y-auto h-[calc(85vh-100px)] pt-4">
            <Tabs defaultValue="insights" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="insights">Insights</TabsTrigger>
                <TabsTrigger value="hypotheses">Hypothesen</TabsTrigger>
                <TabsTrigger value="team">Team</TabsTrigger>
              </TabsList>
              
              <TabsContent value="insights" className="mt-0">
                <InsightsPanel 
                  sessions={activeSessions} 
                  activeTab={preferences.activeInsightsTab}
                  onTabChange={updateActiveInsightsTab}
                  embedded
                />
              </TabsContent>
              
              <TabsContent value="hypotheses" className="mt-0">
                {workspaceId && (
                  <HypothesesTracker workspaceId={workspaceId} embedded />
                )}
              </TabsContent>
              
              <TabsContent value="team" className="mt-0">
                <TeamActivityFeed activities={teamActivities} embedded />
              </TabsContent>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Dashboard;
